import { useEffect, useState } from 'react';
import { DiagramEngine, PortWidget } from '@projectstorm/react-diagrams-core';
import { DataNodeModel, NodeKind } from './DataNodeModel';
import './DataNodeWidget.scss';
import _ from 'lodash';
import { DataPortModel } from '../ports/DataPortModel';
import { DataSidePortModel } from '../ports/DataSidePortModel';
import { classNames } from 'primereact/utils';
// @ts-ignore
import { Resizable } from 'react-resizable';
import { InputTextarea } from 'primereact/inputtextarea';
import { InputSwitch } from 'primereact/inputswitch';
import { useAppSelector } from '../../../../hooks/hooks';
import { NodeRunningState } from '../../sidebar/panels/NodeConfiguration';
import { PipelineState } from '../../editorSlice';


export interface NodeWidgetProps {
    options: { showSidePanel: () => void; };
    node: DataNodeModel;
    engine: DiagramEngine;
}

export const DataNodeWidget = ({options, node, engine}: NodeWidgetProps) => {

    const { sizePanel, current } = useAppSelector((state) => state.editor);
    const [runningState, setRunningState] = useState<NodeRunningState>();
    const pipelineState = useAppSelector((state) =>  state.editor.pipelineState);

    const [isDisabled, setIsDisabled] = useState<boolean>(() => {
        return node.getIsDisabled()
    });

    useEffect(() => {
      updateRunningState();
    }, [current]);

    const renderDataPort = (port: DataPortModel) => {
        return <PortWidget engine={engine} port={port} key={"port-" + port.getID()}>
            <div className={classNames("circle-port", Object.keys(port.getLinks()).length > 0 && "port-connected")}
                 title={port.getName() + (port.getOptions().label ? " - " + port.getOptions().label: '')}>
                <div className="port-name" />
            </div>
        </PortWidget>;
    };

    const renderSideOutPort = (port: DataSidePortModel) => {
        const links = Object.keys(port.getLinks())
        return <PortWidget engine={engine} port={port} key={"sideout-" + port.getID()}>
            <div className={classNames("circle-port" ,  links.length > 0 && "port-connected")}
                 title={port.getName()}>
                <div className="port-name"/>
            </div>
        </PortWidget>
    };

    const renderIcon = () => {
        return <div className="icon" draggable={false}>
            <img src={node.schema?.icon} alt="" draggable={false}/>
        </div>
    }

    const unlockNode = () => {
        node.setLocked(false);
        node.fireEvent({ }, "NodesUpdated");
        window.removeEventListener("mouseup", unlockNode);
    }

    const onResize = (e: MouseEvent) => {
        if (!node.isLocked()) {
            window.addEventListener("mouseup", unlockNode);
            node.setLocked(true);
        }
        const newWidth = node.width+e.movementX;
        const newHeight = node.height+e.movementY;
        node.width = newWidth < node.minWidth ? node.minWidth : newWidth;
        node.height = newHeight < node.minHeight ? node.minHeight : newHeight;
    };

    const updateRunningState = () => {
      if (current) {
        try {
          const topologyData = JSON.parse(current.data);

          const nodeData = topologyData.find((data: any) => data.id === node.getID());

          if (!nodeData) {
            throw new Error(`No node found with ID ${node.getID()}`);
          }

          const isPaused = nodeData.meta?.pause ?? false;
          setRunningState(isPaused ? NodeRunningState.PAUSED : NodeRunningState.RUNNING);

        } catch (error) {
          console.error('Error while parsing or updating running state:', error);
        }
      }
    };

    const RunningStateIndicator = () => {
      const isPaused = runningState === NodeRunningState.PAUSED;
      const isRunning = runningState === NodeRunningState.RUNNING;
      return (
        <div className={"nodeRunningState"}>
          <div className={"iconContainer state_pause"} style={{visibility: isPaused  && current?.runtimeMode == "basic" ? 'visible' : 'hidden'}}>
            <i className="pi pi-pause"/>
          </div>
          <div className={"iconContainer state_running"} style={{visibility: isRunning && current?.runtimeMode == "basic" ? 'visible' : 'hidden'}}>
            <i className="pi pi-play"/>
          </div>
          <div className={"iconContainer state_loading"} style={{visibility: (!isPaused && !isRunning && current?.runtimeMode == "basic") ? 'visible' : 'hidden'}}>
            <i className="pi pi-spin pi-spinner"/>
          </div>
        </div>
      );
    };

    const nodeVersion = (node: DataNodeModel) => {
        const version = (
            node?.settings?.["image"] || 
            node.getDefinition()?.settings?.["image"]?.default_value || 
            "").split(":").pop();
        return version ? ` v${version}` : '';
    }

    // NOTE! height is same as grid layout!
    // console.log('connector node factory event', node)
    return <Resizable height={node.height} width={node.width} onResize={onResize}>
        <div style={{width: node.width + 'px', height: node.height + 'px'}}
            className={`connector-node kind__${node.getKind()} ${(node.isSelected() ? 'node__selected' : '')} ${(node.isDisabled ? 'node__disabled' : '')}`}
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        // show and update the side panel only if already opened
                        if (sizePanel !== "100%") {
                            options.showSidePanel();
                        }
                    }}
                    onDoubleClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        // show and update the side panel
                        options.showSidePanel();
                    }}>
            <div className="ports">
                {node.kind !== NodeKind.NOTE && <div className='port__in' >
                {_.map(node.getInPorts(), renderDataPort)}
                </div>}
                <div className='port__title'>
                    {node.kind !== NodeKind.NOTE ? <>
                        <div className="typeNode">
                            {renderIcon()}
                            <div>{node.getTechnicalName()}{nodeVersion(node)}</div>
                        </div>
                        <div className={"nodeName"}> {node.getOptions().name} </div>
                        {pipelineState === PipelineState.RUNNING && RunningStateIndicator()}
                    </> :
                    <InputTextarea className={"textArea w-full h-full"}
                                   spellCheck={false} readOnly={true}
                                   value={node.getOptions().name} />}
                </div>
                {node.kind !== NodeKind.NOTE && <div  className='port__out'>
                    {_.map(node.getDataOutPorts(), renderDataPort)}
                    {_.map(node.getSideOutPorts(), renderSideOutPort)}
                </div>}
            </div>
        </div>
    </Resizable>;
}
